import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'
import waterVertexShader from './shaders/water/vertex.glsl'
import waterFragmentShader from './shaders/water/fragment.glsl'

/**
 * Loader
 */
const loaderOverlay = document.querySelector('.loader-overlay')
const loaderProgress = document.querySelector('.loader-progress')
const loaderPercentage = document.querySelector('.loader-percentage')

let progress = 0
const loadingInterval = setInterval(() => {
    progress += Math.random() * 15
    if (progress >= 99) {
        progress = 99
        clearInterval(loadingInterval)

        setTimeout(() => {
            loaderProgress.style.width = '100%'
            loaderPercentage.textContent = '100%'

            setTimeout(() => {
                loaderOverlay.classList.add('loaded')
                setTimeout(() => {
                    loaderOverlay.style.display = 'none'
                }, 600)
            }, 200)
        }, 1000)
    }
    loaderProgress.style.width = `${progress}%`
    loaderPercentage.textContent = `${Math.floor(progress)}%`
}, 100)

/**
 * Base
 */
const gui = new GUI({ width: 340, title: 'Controls' })
const debugObject = {}

const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

/**
 * Sky Gradient Background
 */
const skyGeometry = new THREE.SphereGeometry(50, 32, 32)
const skyMaterial = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    uniforms: {
        uTopColor: { value: new THREE.Color('#0a0a1a') },
        uBottomColor: { value: new THREE.Color('#1a4a6a') },
        uOffset: { value: 0.4 },
        uExponent: { value: 0.6 }
    },
    vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform vec3 uTopColor;
        uniform vec3 uBottomColor;
        uniform float uOffset;
        uniform float uExponent;
        varying vec3 vWorldPosition;

        void main() {
            float h = normalize(vWorldPosition + uOffset).y;
            float t = max(pow(max(h, 0.0), uExponent), 0.0);
            vec3 color = mix(uBottomColor, uTopColor, t);
            gl_FragColor = vec4(color, 1.0);
        }
    `
})
const sky = new THREE.Mesh(skyGeometry, skyMaterial)
scene.add(sky)

/**
 * Moon
 */
const moonGeometry = new THREE.SphereGeometry(0.3, 32, 32)
const moonMaterial = new THREE.ShaderMaterial({
    uniforms: {
        uTime: { value: 0 }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        varying vec2 vUv;
        void main() {
            vec2 center = vUv - 0.5;
            float dist = length(center);

            // Soft glow
            float glow = 1.0 - smoothstep(0.0, 0.5, dist);

            // Moon color - warm white/yellow
            vec3 moonColor = vec3(1.0, 0.98, 0.9);
            vec3 glowColor = vec3(0.9, 0.95, 1.0);

            vec3 color = mix(glowColor, moonColor, glow);
            float alpha = glow;

            gl_FragColor = vec4(color, alpha);
        }
    `,
    transparent: true,
    depthWrite: false
})
const moon = new THREE.Mesh(moonGeometry, moonMaterial)
moon.position.set(3, 1.5, -5)
scene.add(moon)

// Moon glow
const moonGlowGeometry = new THREE.PlaneGeometry(3, 3)
const moonGlowMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {},
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        varying vec2 vUv;
        void main() {
            vec2 center = vUv - 0.5;
            float dist = length(center);

            float glow = 1.0 - smoothstep(0.0, 0.5, dist);
            glow = pow(glow, 2.0);

            vec3 color = vec3(0.6, 0.8, 1.0);
            gl_FragColor = vec4(color, glow * 0.3);
        }
    `
})
const moonGlow = new THREE.Mesh(moonGlowGeometry, moonGlowMaterial)
moonGlow.position.copy(moon.position)
moonGlow.lookAt(0, 0, 0)
scene.add(moonGlow)

/**
 * Stars - ko'p, miltillamaydigan
 */
const starsGeometry = new THREE.BufferGeometry()
const starsCount = 1000
const starsPositions = new Float32Array(starsCount * 3)
const starsSizes = new Float32Array(starsCount)
const starsColors = new Float32Array(starsCount * 3)

for(let i = 0; i < starsCount; i++) {
    const i3 = i * 3

    // Sphere distribution
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)
    const radius = 30 + Math.random() * 15

    starsPositions[i3] = radius * Math.sin(phi) * Math.cos(theta)
    starsPositions[i3 + 1] = Math.abs(radius * Math.cos(phi)) * 0.5 + 0.5 // Faqat yuqorida
    starsPositions[i3 + 2] = radius * Math.sin(phi) * Math.sin(theta)

    // Turli o'lchamlar
    starsSizes[i] = Math.random() * 0.8 + 0.2

    // Turli ranglar - oq, ko'kish, sariqish
    const colorChoice = Math.random()
    if (colorChoice < 0.7) {
        // Oq
        starsColors[i3] = 1.0
        starsColors[i3 + 1] = 1.0
        starsColors[i3 + 2] = 1.0
    } else if (colorChoice < 0.85) {
        // Ko'kish
        starsColors[i3] = 0.7
        starsColors[i3 + 1] = 0.85
        starsColors[i3 + 2] = 1.0
    } else {
        // Sariqish
        starsColors[i3] = 1.0
        starsColors[i3 + 1] = 0.9
        starsColors[i3 + 2] = 0.7
    }
}

starsGeometry.setAttribute('position', new THREE.BufferAttribute(starsPositions, 3))
starsGeometry.setAttribute('aSize', new THREE.BufferAttribute(starsSizes, 1))
starsGeometry.setAttribute('aColor', new THREE.BufferAttribute(starsColors, 3))

const starsMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {},
    vertexShader: `
        attribute float aSize;
        attribute vec3 aColor;
        varying vec3 vColor;
        varying float vSize;

        void main() {
            vec4 modelPosition = modelMatrix * vec4(position, 1.0);
            vec4 viewPosition = viewMatrix * modelPosition;
            gl_Position = projectionMatrix * viewPosition;

            gl_PointSize = aSize * 15.0 * (1.0 / -viewPosition.z);
            vColor = aColor;
            vSize = aSize;
        }
    `,
    fragmentShader: `
        varying vec3 vColor;
        varying float vSize;

        void main() {
            float dist = length(gl_PointCoord - 0.5);
            float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
            alpha *= 0.8;

            gl_FragColor = vec4(vColor, alpha);
        }
    `
})

const stars = new THREE.Points(starsGeometry, starsMaterial)
scene.add(stars)

/**
 * Milky Way Galaxy Effect
 */
const galaxyGeometry = new THREE.BufferGeometry()
const galaxyCount = 1500
const galaxyPositions = new Float32Array(galaxyCount * 3)
const galaxySizes = new Float32Array(galaxyCount)

for(let i = 0; i < galaxyCount; i++) {
    const i3 = i * 3

    // Galaxy band across sky
    const angle = (Math.random() - 0.5) * Math.PI * 0.4
    const spread = (Math.random() - 0.5) * 2
    const radius = 25 + Math.random() * 10

    const baseAngle = Math.PI * 0.3 // Galaxy tilted

    galaxyPositions[i3] = radius * Math.cos(angle + baseAngle) + spread * 3
    galaxyPositions[i3 + 1] = radius * Math.sin(angle) * 0.3 + 3 + Math.random() * 2
    galaxyPositions[i3 + 2] = radius * Math.sin(angle + baseAngle) * 0.5 - 10

    galaxySizes[i] = Math.random() * 0.5 + 0.1
}

galaxyGeometry.setAttribute('position', new THREE.BufferAttribute(galaxyPositions, 3))
galaxyGeometry.setAttribute('aSize', new THREE.BufferAttribute(galaxySizes, 1))

const galaxyMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {},
    vertexShader: `
        attribute float aSize;
        varying float vAlpha;

        void main() {
            vec4 modelPosition = modelMatrix * vec4(position, 1.0);
            vec4 viewPosition = viewMatrix * modelPosition;
            gl_Position = projectionMatrix * viewPosition;

            gl_PointSize = aSize * 20.0 * (1.0 / -viewPosition.z);
            vAlpha = aSize;
        }
    `,
    fragmentShader: `
        varying float vAlpha;

        void main() {
            float dist = length(gl_PointCoord - 0.5);
            float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
            alpha *= vAlpha * 0.4;

            vec3 color = mix(vec3(0.8, 0.7, 1.0), vec3(1.0, 0.9, 0.95), vAlpha);
            gl_FragColor = vec4(color, alpha);
        }
    `
})

const galaxy = new THREE.Points(galaxyGeometry, galaxyMaterial)
scene.add(galaxy)

/**
 * Water
 */
const waterGeometry = new THREE.PlaneGeometry(3, 3, 128, 128)
waterGeometry.deleteAttribute('normal')
waterGeometry.deleteAttribute('uv')

debugObject.depthColor = '#000510'
debugObject.surfaceColor = '#1a5a8a'

gui.addColor(debugObject, 'depthColor').onChange(() => {
    waterMaterial.uniforms.uDepthColor.value.set(debugObject.depthColor)
})
gui.addColor(debugObject, 'surfaceColor').onChange(() => {
    waterMaterial.uniforms.uSurfaceColor.value.set(debugObject.surfaceColor)
})

const waterMaterial = new THREE.ShaderMaterial({
    vertexShader: waterVertexShader,
    fragmentShader: waterFragmentShader,
    uniforms:
    {
        uTime: { value: 0 },

        uBigWavesElevation: { value: 0.1 },
        uBigWavesFrequency: { value: new THREE.Vector2(4, 1.5) },
        uBigWavesSpeed: { value: 0.5 },

        uSmallWavesElevation: { value: 0.08 },
        uSmallWavesFrequency: { value: 3 },
        uSmallWavesSpeed: { value: 0.2 },
        uSmallIterations: { value: 3 },

        uDepthColor: { value: new THREE.Color(debugObject.depthColor) },
        uSurfaceColor: { value: new THREE.Color(debugObject.surfaceColor) },
        uColorOffset: { value: 0.1 },
        uColorMultiplier: { value: 4 }
    }
})

gui.add(waterMaterial.uniforms.uBigWavesElevation, 'value').min(0).max(1).step(0.001).name('uBigWavesElevation')
gui.add(waterMaterial.uniforms.uBigWavesFrequency.value, 'x').min(0).max(10).step(0.001).name('uBigWavesFrequencyX')
gui.add(waterMaterial.uniforms.uBigWavesFrequency.value, 'y').min(0).max(10).step(0.001).name('uBigWavesFrequencyY')
gui.add(waterMaterial.uniforms.uBigWavesSpeed, 'value').min(0).max(4).step(0.001).name('uBigWavesSpeed')

gui.add(waterMaterial.uniforms.uSmallWavesElevation, 'value').min(0).max(1).step(0.001).name('uSmallWavesElevation')
gui.add(waterMaterial.uniforms.uSmallWavesFrequency, 'value').min(0).max(30).step(0.001).name('uSmallWavesFrequency')
gui.add(waterMaterial.uniforms.uSmallWavesSpeed, 'value').min(0).max(4).step(0.001).name('uSmallWavesSpeed')
gui.add(waterMaterial.uniforms.uSmallIterations, 'value').min(0).max(5).step(1).name('uSmallIterations')

gui.add(waterMaterial.uniforms.uColorOffset, 'value').min(0).max(1).step(0.001).name('uColorOffset')
gui.add(waterMaterial.uniforms.uColorMultiplier, 'value').min(0).max(10).step(0.001).name('uColorMultiplier')

const water = new THREE.Mesh(waterGeometry, waterMaterial)
water.rotation.x = - Math.PI * 0.5
scene.add(water)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.set(1.19, 0.13, 1.09)
scene.add(camera)

/**
 * Controls - QULFLANGAN (faqat X axis)
 */
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05
controls.enablePan = false
controls.enableZoom = false

const currentPolar = Math.acos(camera.position.y / camera.position.length())
controls.minPolarAngle = currentPolar
controls.maxPolarAngle = currentPolar
controls.target.set(0, 0, 0)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    powerPreference: 'high-performance'
})
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.0
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor('#0a0a1a')

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Water
    waterMaterial.uniforms.uTime.value = elapsedTime

    // Moon glow looks at camera
    moonGlow.lookAt(camera.position)

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    window.requestAnimationFrame(tick)
}

tick()
